import { useUser } from "@clerk/clerk-react";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type Status = "loading" | "complete" | "incomplete";

/**
 * Returns whether the signed-in parent has completed onboarding.
 * "Complete" = profiles.onboarded_at IS NOT NULL AND profiles.tos_accepted_at IS NOT NULL.
 * Used by ProtectedRoute to send users to /onboarding if they haven't.
 */
export function useOnboardingStatus(): { status: Status } {
  const { user, isLoaded } = useUser();
  const [status, setStatus] = useState<Status>("loading");

  // Same loop guard as useSubscription + Onboarding hydration: Clerk's `user`
  // ref churns every render, so `[user, isLoaded]`-shaped deps re-fire the
  // poll endlessly. setStatus inside the poll re-renders ProtectedRoute,
  // which would unmount/remount the active route, restarting Onboarding's
  // hydration spinner. Lock poll to ONE run per externalId.
  const polledForUidRef = useRef<string | null>(null);
  const supabaseUuid = user?.externalId ?? null;

  useEffect(() => {
    if (!isLoaded) return;
    if (!supabaseUuid) return; // bridge not yet resolved
    if (polledForUidRef.current === supabaseUuid) return;
    polledForUidRef.current = supabaseUuid;

    // Fast-path: Onboarding sets this flag immediately before navigating
    // to /hub. Skips the racy re-read of profiles.onboarded_at.
    try {
      if (sessionStorage.getItem(`tm-just-onboarded:${supabaseUuid}`) === "1") {
        setStatus("complete");
        return;
      }
    } catch {}

    let cancelled = false;
    let attempt = 0;
    const poll = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("onboarded_at, tos_accepted_at")
        .eq("user_id", supabaseUuid)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        attempt++;
        if (attempt < 5) {
          setTimeout(poll, 800 * attempt);
          return;
        }
        setStatus("complete");
        return;
      }
      const p = data as any;
      const onboarded = !!p?.onboarded_at && !!p?.tos_accepted_at;
      setStatus(onboarded ? "complete" : "incomplete");
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, supabaseUuid]);

  return { status };
}
